import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import {
  createProfile,
  getEventsDetails,
  getEventsDetailsBySlug,
  getSubscriptionEventConfig,
  joinEvent,
  loginSendOTP,
  verifyOTP,
} from "../api"
import { toast } from "sonner"
import Cookies from "js-cookie"

// Get Event Details
export const getEventDetails = createAsyncThunk(
  "getEventDetails",
  async (eventId: string, { rejectWithValue }) => {
    try {
      const response = await getEventsDetails(eventId)
      return response
    } catch (error: any) {
      toast.error("Error Fetching Event Details")
      return rejectWithValue(error.response)
    }
  }
)

// Get Event Details by slug — resolves slug to event data without changing the URL
export const getEventDetailsBySlug = createAsyncThunk(
  "getEventDetailsBySlug",
  async (slug: string, { rejectWithValue }) => {
    try {
      const slugResponse = await getEventsDetailsBySlug(slug)
      if (slugResponse?.status !== "OK" || !slugResponse?.body?.id) {
        return rejectWithValue("Event not found")
      }
      const response = await getEventsDetails(slugResponse.body.id)
      return response
    } catch (error: any) {
      toast.error("Error Fetching Event Details")
      return rejectWithValue(error.response)
    }
  }
)

// Send Otp
export const sendOtp = createAsyncThunk(
  "sendOtp",
  async (number: string, { rejectWithValue }) => {
    try {
      const response = await loginSendOTP(number)
      return response
    } catch (error: any) {
      toast.error("Error Sending OTP")
      return rejectWithValue(error.response)
    }
  }
)

// Verify Otp
export const verifyOtp = createAsyncThunk(
  "verifyOtp",
  async (data: { number: string; otp: string }, { rejectWithValue }) => {
    try {
      const response = await verifyOTP(data?.number, data?.otp)
      return response
    } catch (error: any) {
      toast.error("Error Verifying OTP")
      return rejectWithValue(error.response)
    }
  }
)

// Get Config
export const getEventConfig = createAsyncThunk(
  "getEventConfig",
  async (eventId: string, { rejectWithValue }) => {
    try {
      const response = await getSubscriptionEventConfig(eventId)
      return response
    } catch (error: any) {
      toast.error("Error Fetching Event Configs")
      return rejectWithValue(error.response)
    }
  }
)

// Create Profile
export const createUserProfile = createAsyncThunk(
  "createUserProfile",
  async (data: { number: string; firstName: string; lastName: string }, { rejectWithValue }) => {
    try {
      const response = await createProfile(data.number, data.firstName, data.lastName)
      return response
    } catch (error: any) {
      toast.error("Failed to create profile")
      return rejectWithValue(error.response)
    }
  }
)

// Join Event
export const joinEventAction = createAsyncThunk(
  "joinEvent",
  async (data: { eventId: string; userId: string }, { rejectWithValue }) => {
    try {
      const response = await joinEvent(data.eventId, data.userId)
      return response
    } catch (error: any) {
      toast.error("Failed to join event")
      return rejectWithValue(error.response)
    }
  }
)

// Get User By Phone Number
// export const getUserDetails = createAsyncThunk(
//   "getUserDetails",
//   async (number: string, { rejectWithValue }) => {
//     try {
//       const response = await getUserByPhoneNumber(number)
//       return response
//     } catch (error: any) {
//       toast.error("Error Fetching Event Configs")
//       return rejectWithValue(error.response)
//     }
//   }
// )

const initialState: {
  data: any
  config: any
  // user: any
  userId: string | null
  eventId: string | null
  verified: boolean
  category: string
  number: string
  otpSent: boolean
  loading: boolean
  error: any
  status: string
} = {
  data: "null",
  config: undefined,
  // user: null,
  status: "",
  error: null,
  loading: false,
  userId: null,
  eventId: null,
  verified: false,
  category: "",
  number: "",
  otpSent: false,
}

const eventSlice = createSlice({
  name: "Event",
  initialState,
  reducers: {
    setEventVerified: (state) => {
      state.verified = true
    },

    setPhoneNumber: (state, action) => {
      state.number = action.payload
    },

    setCategory: (state, action) => {
      state.category = action.payload
    },

    setUserId: (state, action) => {
      state.userId = action.payload
    },

    clearOtpSent: (state) => {
      state.otpSent = false
    },

    logoutEvent: (state) => {
      state = initialState
    },
  },
  extraReducers: (builder) => {
    // Get Event Details
    builder.addCase(getEventDetails.pending, (state) => {
      state.status = "pending"
      state.loading = true
    })
    builder.addCase(getEventDetails.fulfilled, (state, action) => {
      state.loading = false
      state.status = action.payload.status
      const body = action.payload.body
      if (body?.coverPhotoLink && body?.modifiedDate) {
        body.coverPhotoLink = `${body.coverPhotoLink}?v=${body.modifiedDate}`
      }
      state.data = body
      state.error = action.payload.error
      state.eventId = action.payload.body?.id

      if (action.payload.error || action.payload.status === "ERROR")
        toast.error(action.payload.error || "Error Fetching Event Details")
    })
    builder.addCase(getEventDetails.rejected, (state) => {
      state.error = "Error fetching data"
      state.loading = false
      state.status = "ERROR"
    })

    // Get Event Details by slug — same state shape as getEventDetails
    builder.addCase(getEventDetailsBySlug.pending, (state) => {
      state.status = "pending"
      state.loading = true
    })
    builder.addCase(getEventDetailsBySlug.fulfilled, (state, action) => {
      state.loading = false
      state.status = action.payload.status
      const body = action.payload.body
      if (body?.coverPhotoLink && body?.modifiedDate) {
        body.coverPhotoLink = `${body.coverPhotoLink}?v=${body.modifiedDate}`
      }
      state.data = body
      state.error = action.payload.error
      state.eventId = action.payload.body?.id
      if (action.payload.error || action.payload.status === "ERROR")
        toast.error(action.payload.error || "Error Fetching Event Details")
    })
    builder.addCase(getEventDetailsBySlug.rejected, (state) => {
      state.error = "Event not found"
      state.loading = false
      state.status = "ERROR"
    })

    // Send OTP
    builder.addCase(sendOtp.pending, (state) => {
      state.status = "pending"
      state.loading = true
    })
    builder.addCase(sendOtp.fulfilled, (state, action) => {
      state.loading = false
      state.status = action.payload.status
      state.error = action.payload.error
      if (action.payload.status === "OK") {
        state.otpSent = true
        toast.success("OTP sent successfully to " + state.number)
      }

      if (action.payload.error || action.payload.status === "ERROR")
        toast.error(
          action.payload.error ||
            action.payload.body ||
            "Error Sending OTP to " + state.number
        )
    })
    builder.addCase(sendOtp.rejected, (state) => {
      state.error = "Error sending otp"
      state.loading = false
      state.status = "ERROR"
    })

    // Verify OTP
    builder.addCase(verifyOtp.pending, (state) => {
      state.status = "pending"
      state.loading = true
    })
    builder.addCase(verifyOtp.fulfilled, (state, action) => {
      state.loading = false
      state.status = action.payload.status
      state.error = action.payload.error
      if (action.payload.status === "OK") {
        if (action.payload.body?.id) {
          state.userId = action.payload.body.id
        }

        Cookies.set(
          `${process.env.REACT_APP_IFE_WEB_GALLERY_COOKIE}?EVENT=${state.eventId}` ||
            "",
          JSON.stringify({
            eventId: state.eventId,
            number: state.number,
            userId: action.payload.body?.id || state.userId,
          }),
          {
            expires: 90,
            path: "/",
          }
        )

        // Only set verified to true if user exists (has ID)
        if (action.payload.body?.id) {
          state.verified = true
          state.otpSent = false
        }
        // If user doesn't exist, keep otpSent = true so we stay in VerifyOtp component

        // If user ID is returned in the response, call join event
        if (action.payload.body?.id && state.eventId) {
          // This will be handled by the component that dispatches the action
          // since we can't dispatch another action from here
        }
      }

      if (action.payload.error || action.payload.status === "ERROR")
        toast.error(
          action.payload.error || action.payload.body || "Error Verifying OTP"
        )
    })
    builder.addCase(verifyOtp.rejected, (state) => {
      state.error = "Error verifying otp"
      state.loading = false
      state.status = "ERROR"
    })

    // Get Config
    builder.addCase(getEventConfig.pending, (state) => {
      state.status = "pending"
      state.loading = true
    })
    builder.addCase(getEventConfig.fulfilled, (state, action) => {
      state.loading = false
      state.status = action.payload.status
      state.error = action.payload.error
      state.config = action.payload.body

      if (action.payload.error || action.payload.status === "ERROR")
        toast.error(
          action.payload.error ||
            action.payload.body ||
            "Error Fetching Event Config"
        )
    })
    builder.addCase(getEventConfig.rejected, (state) => {
      state.error = "Error fetching event Config"
      state.loading = false
      state.status = "ERROR"
    })

    // Join Event
    builder.addCase(joinEventAction.pending, (state) => {
      state.status = "pending"
      state.loading = true
    })
    builder.addCase(joinEventAction.fulfilled, (state, action) => {
      state.loading = false
      state.status = action.payload.status
      state.error = action.payload.error

      if (action.payload.status === "OK") {
        toast.success("Successfully joined event")
      }

      if (action.payload.error || action.payload.status === "ERROR")
        toast.error(
          action.payload.error ||
            action.payload.body ||
            "Failed to join event"
        )
    })
    builder.addCase(joinEventAction.rejected, (state) => {
      state.error = "Failed to join event"
      state.loading = false
      state.status = "ERROR"
    })

    // Create User Profile
    builder.addCase(createUserProfile.pending, (state) => {
      state.status = "pending"
      state.loading = true
    })
    builder.addCase(createUserProfile.fulfilled, (state, action) => {
      state.loading = false
      state.status = action.payload.status
      state.error = action.payload.error

      if (action.payload.status === "OK") {
        state.verified = true
        state.otpSent = false
        toast.success("Profile created successfully")
      }

      if (action.payload.error || action.payload.status === "ERROR")
        toast.error(
          action.payload.error ||
            action.payload.body ||
            "Failed to create profile"
        )
    })
    builder.addCase(createUserProfile.rejected, (state) => {
      state.error = "Failed to create profile"
      state.loading = false
      state.status = "ERROR"
    })

    // Get User Details
    // builder.addCase(getUserDetails.pending, (state) => {
    //   state.status = "pending"
    //   state.loading = true
    // })
    // builder.addCase(getUserDetails.fulfilled, (state, action) => {
    //   state.loading = false
    //   state.status = action.payload.status
    //   state.error = action.payload.error
    //   state.user = action.payload.body
    //   state.userId = action.payload.body?.id

    //   if (action.payload.error || action.payload.status === "ERROR")
    //     toast.error(
    //       action.payload.error ||
    //         action.payload.body ||
    //         "Error fetching User Details"
    //     )
    // })
    // builder.addCase(getUserDetails.rejected, (state) => {
    //   state.error = "Error fetching User Details"
    //   state.loading = false
    //   state.status = "ERROR"
    // })
  },
})

export const {
  setEventVerified,
  setPhoneNumber,
  setCategory,
  setUserId,
  logoutEvent,
  clearOtpSent,
} = eventSlice.actions
export default eventSlice.reducer
